/**
 * @file STATUS of one invoice row: Paid when outstanding is zero; Overdue when
 * its bucket is `days61To90` or `daysOver90`; Partially paid when outstanding
 * is above zero and below invoiced; Outstanding otherwise. Consumer: the
 * Invoicing tab's invoice rows. Amounts are decimal strings compared exactly,
 * never as floats. Null when an amount the answer needs is refused, so the
 * cell refuses in place rather than guessing.
 */
import type { AnalyticsAgingBucket } from '#types';

import { analyticsDecimalCompare } from './analyticsDecimalCompare';
import type {
  AnalyticsInvoiceRow,
  AnalyticsInvoiceStatus,
} from './analyticsInvoicingTab.types';

const OVERDUE_BUCKETS: readonly AnalyticsAgingBucket[] = [
  'days61To90',
  'daysOver90',
];

export const analyticsInvoiceStatus = ({
  value,
  outstanding,
  bucket,
}: Pick<
  AnalyticsInvoiceRow,
  'value' | 'outstanding' | 'bucket'
>): AnalyticsInvoiceStatus | null => {
  if (outstanding === null) return null;
  if (analyticsDecimalCompare(outstanding, '0') === 0) return 'Paid';
  if (bucket !== null && OVERDUE_BUCKETS.includes(bucket)) return 'Overdue';
  if (value === null) return null;
  return analyticsDecimalCompare(outstanding, '0') > 0 &&
    analyticsDecimalCompare(outstanding, value) < 0
    ? 'Partially paid'
    : 'Outstanding';
};
